import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaFile, FaUpload, FaCalendarAlt, FaUsers, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import config from '../config/api';

const CreateHomework = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [file, setFile] = useState(null);
  const [answerKey, setAnswerKey] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingStudents, setLoadingStudents] = useState(true);
  const navigate = useNavigate();

  const allowedFileTypes = ['pdf', 'doc', 'docx', 'txt', 'png', 'jpg', 'jpeg'];

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoadingStudents(true);
        const response = await axios.get(`${config.apiURL}/teacher/students`);
        setStudents(response.data.students);
      } catch (err) {
        console.error('Erro ao carregar estudantes:', err);
        setError('Erro ao carregar lista de estudantes');
      } finally {
        setLoadingStudents(false);
      }
    };

    fetchStudents();
  }, []);

  const handleFileChange = (e, setFileFunction) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.name.split('.').pop().toLowerCase();
      if (allowedFileTypes.includes(fileType)) {
        setFileFunction(selectedFile);
        setError('');
      } else {
        setError('Tipo de arquivo não permitido. Use: PDF, DOC, DOCX, TXT, PNG, JPG, JPEG');
      }
    }
  };

  const handleStudentSelect = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllStudents = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(student => student.id));
    }
  };

  const categorizeStudentsByAge = () => {
    const categories = {
      '6-8 anos': [],
      '9-11 anos': [],
      '12-14 anos': [],
      '15-17 anos': [],
      '18+ anos': [],
      'Não informado': []
    };

    students.forEach(student => {
      const age = student.age;
      if (!age) {
        categories['Não informado'].push(student);
      } else if (age >= 6 && age <= 8) {
        categories['6-8 anos'].push(student);
      } else if (age >= 9 && age <= 11) {
        categories['9-11 anos'].push(student);
      } else if (age >= 12 && age <= 14) {
        categories['12-14 anos'].push(student);
      } else if (age >= 15 && age <= 17) {
        categories['15-17 anos'].push(student);
      } else {
        categories['18+ anos'].push(student);
      }
    });

    return categories;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !description || !subject || !dueDate || selectedStudents.length === 0) {
      setError('Por favor, preencha todos os campos obrigatórios e selecione pelo menos um estudante.');
      return;
    }

    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('subject', subject);
    formData.append('due_date', dueDate);
    formData.append('students', JSON.stringify(selectedStudents));
    
    if (file) {
      formData.append('homework_file', file);
    }
    
    if (answerKey) {
      formData.append('answer_key', answerKey);
    }

    try {
      setLoading(true);
      const response = await axios.post(`${config.apiURL}/teacher/create-homework`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        navigate('/homeworks');
      } else {
        setError(response.data.message || 'Erro ao criar lição');
      }
    } catch (err) {
      console.error('Erro ao criar lição:', err);
      setError('Erro ao criar lição. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const renderStudentsByCategory = (categories) => {
    return Object.entries(categories).map(([ageGroup, studentsInGroup]) => {
      if (studentsInGroup.length === 0) return null;
      
      return (
        <div key={ageGroup} className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
            <FaUsers className="mr-2 text-blue-600" />
            {ageGroup} ({studentsInGroup.length} alunos)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {studentsInGroup.map(student => (
              <div key={student.id} className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors">
                <input
                  type="checkbox"
                  id={`student-${student.id}`}
                  checked={selectedStudents.includes(student.id)}
                  onChange={() => handleStudentSelect(student.id)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor={`student-${student.id}`} className="flex-1 cursor-pointer">
                  <div className="font-medium text-gray-900">{student.name}</div>
                  <div className="text-sm text-gray-500">
                    {student.age ? `${student.age} anos` : 'Idade não informada'}
                  </div>
                </label>
              </div>
            ))}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <FaPlus className="text-blue-600" />
            <span>Criar Nova Lição</span>
          </h1>
          <p className="mt-2 text-gray-600">
            Crie uma nova lição de casa para seus estudantes
          </p>
        </div>

        <div className="bg-white shadow-lg rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título da Lição *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Digite o título da lição"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Matéria *
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Digite a matéria"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Descreva a lição de casa..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Entrega *
              </label>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Arquivo da Lição
                </label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer bg-blue-50 border border-blue-200 rounded-md px-4 py-2 hover:bg-blue-100 transition-colors">
                    <FaFile className="text-blue-600" />
                    <span className="text-blue-600 text-sm">
                      {file ? file.name : 'Selecionar arquivo'}
                    </span>
                    <input
                      type="file"
                      onChange={(e) => handleFileChange(e, setFile)}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                    />
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gabarito (Opcional)
                </label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer bg-green-50 border border-green-200 rounded-md px-4 py-2 hover:bg-green-100 transition-colors">
                    <FaFile className="text-green-600" />
                    <span className="text-green-600 text-sm">
                      {answerKey ? answerKey.name : 'Selecionar gabarito'}
                    </span>
                    <input
                      type="file"
                      onChange={(e) => handleFileChange(e, setAnswerKey)}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-gray-700">
                  Selecionar Estudantes * ({selectedStudents.length} selecionados)
                </label>
                <button
                  type="button"
                  onClick={selectAllStudents}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  {selectedStudents.length === students.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                </button>
              </div>

              {loadingStudents ? (
                <div className="flex justify-center items-center h-32">
                  <FaSpinner className="animate-spin h-8 w-8 text-blue-600" />
                </div>
              ) : students.length > 0 ? (
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                  {renderStudentsByCategory(categorizeStudentsByAge())}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Nenhum estudante encontrado. Crie estudantes primeiro.
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/homeworks')}
                className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Criando...</span>
                  </>
                ) : (
                  <>
                    <FaUpload />
                    <span>Criar Lição</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateHomework; 